import { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Wallet, ArrowDownCircle, Clock, CheckCircle, XCircle,
  Building2, Smartphone, Bitcoin, AlertTriangle, Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const WithdrawPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [methods, setMethods] = useState([]);
  const [history, setHistory] = useState([]);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [selectedMethod, setSelectedMethod] = useState("upi");
  const [amount, setAmount] = useState("");
  const [accountDetails, setAccountDetails] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [methodsRes, historyRes, pendingRes] = await Promise.all([
        axios.get(`${API}/withdrawals/methods`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/withdrawals/history`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/withdrawals/pending`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setMethods(methodsRes.data.methods || []);
      setHistory(historyRes.data.withdrawals || []);
      setPendingWithdrawal(pendingRes.data.pending_withdrawal);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const submitWithdrawal = async () => {
    if (!amount || parseFloat(amount) < 100) {
      toast.error("Minimum withdrawal is ₹100");
      return;
    }
    
    if (parseFloat(amount) > (user?.balance || 0)) {
      toast.error("Insufficient balance");
      return;
    }

    const method = methods.find(m => m.id === selectedMethod);
    const missingFields = method?.required_fields?.filter(f => !accountDetails[f]);
    
    if (missingFields?.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/withdrawals/request`, {
        amount: parseFloat(amount),
        method: selectedMethod,
        account_details: accountDetails
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setAmount("");
      setAccountDetails({});
      await refreshBalance();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit withdrawal");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelWithdrawal = async (withdrawalId) => {
    if (!confirm("Are you sure you want to cancel this withdrawal?")) return;
    
    try {
      const response = await axios.post(`${API}/withdrawals/cancel`, {
        withdrawal_id: withdrawalId
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      await refreshBalance();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel withdrawal");
    }
  };

  const methodIcons = {
    bank_transfer: Building2,
    upi: Smartphone,
    crypto: Bitcoin
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#00E701] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const selectedMethodInfo = methods.find(m => m.id === selectedMethod);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00E701] to-[#007AFF] flex items-center justify-center">
              <ArrowDownCircle className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Rajdhani']">Withdraw</h1>
              <p className="text-[#A1A1AA] text-sm">Withdraw your winnings</p>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-[#A1A1AA] text-sm">Available Balance</p>
            <p className="text-2xl font-bold text-[#00E701] font-mono">₹{(user?.balance || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Pending Withdrawal Alert */}
        {pendingWithdrawal && (
          <div className="p-4 rounded-xl bg-[#FFD600]/10 border border-[#FFD600]/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#FFD600]" />
                <div>
                  <p className="text-white font-medium">Pending Withdrawal</p>
                  <p className="text-[#A1A1AA] text-sm">
                    ₹{pendingWithdrawal.amount.toLocaleString()} via {pendingWithdrawal.method}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => cancelWithdrawal(pendingWithdrawal.withdrawal_id)}
                variant="outline"
                size="sm"
                className="border-[#FFD600] text-[#FFD600]"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Withdrawal Form */}
        {!pendingWithdrawal && (
          <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
            <h3 className="text-lg font-bold text-white mb-4">Request Withdrawal</h3>
            
            {/* Method Selection */}
            <div className="mb-6">
              <label className="text-[#A1A1AA] text-sm mb-3 block">Withdrawal Method</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {methods.map((method) => {
                  const Icon = methodIcons[method.id] || Wallet;
                  return (
                    <button
                      key={method.id}
                      onClick={() => {
                        setSelectedMethod(method.id);
                        setAccountDetails({});
                      }}
                      className={`p-4 rounded-xl border transition-all text-left ${
                        selectedMethod === method.id
                          ? 'border-[#00E701] bg-[#00E701]/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className={`w-5 h-5 ${selectedMethod === method.id ? 'text-[#00E701]' : 'text-white/60'}`} />
                        <span className="text-white font-medium">{method.name}</span>
                      </div>
                      <p className="text-[#52525B] text-xs">{method.processing_time}</p>
                      <p className="text-[#52525B] text-xs">
                        Min: ₹{method.min_amount} | Max: ₹{method.max_amount.toLocaleString()}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Amount */}
            <div className="mb-6">
              <label className="text-[#A1A1AA] text-sm mb-2 block">Amount</label>
              <Input
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-12 bg-black/20 border-white/10 text-white font-mono text-lg"
              />
              <div className="flex gap-2 mt-2">
                {[500, 1000, 5000, 10000].map((amt) => (
                  <Button
                    key={amt}
                    variant="outline"
                    size="sm"
                    onClick={() => setAmount(amt.toString())}
                    className="flex-1 border-white/10 text-white/60"
                  >
                    ₹{amt.toLocaleString()}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Account Details */}
            {selectedMethodInfo && (
              <div className="mb-6">
                <label className="text-[#A1A1AA] text-sm mb-2 block">Account Details</label>
                <div className="space-y-3">
                  {selectedMethodInfo.required_fields.map((field) => (
                    <Input
                      key={field}
                      placeholder={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      value={accountDetails[field] || ''}
                      onChange={(e) => setAccountDetails({...accountDetails, [field]: e.target.value})}
                      className="bg-black/20 border-white/10 text-white"
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Fee Display */}
            {amount && parseFloat(amount) > 0 && (
              <div className="p-4 rounded-xl bg-white/5 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-[#A1A1AA]">Amount</span>
                  <span className="text-white">₹{parseFloat(amount).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-[#A1A1AA]">Fee</span>
                  <span className="text-white">
                    {selectedMethod === 'crypto' ? `₹${(parseFloat(amount) * 0.01).toFixed(2)}` : '₹0'}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-2 pt-2 border-t border-white/10">
                  <span className="text-white font-medium">You'll receive</span>
                  <span className="text-[#00E701] font-bold">
                    ₹{(parseFloat(amount) * (selectedMethod === 'crypto' ? 0.99 : 1)).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
            
            <Button
              onClick={submitWithdrawal}
              disabled={submitting || !amount || parseFloat(amount) < 100}
              className="w-full h-12 bg-[#00E701] hover:bg-[#00C201] text-black font-bold"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <ArrowDownCircle className="w-5 h-5 mr-2" />
                  Request Withdrawal
                </>
              )}
            </Button>
          </div>
        )}

        {/* Withdrawal History */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4">Withdrawal History</h3>
          
          {history.length === 0 ? (
            <p className="text-[#52525B] text-center py-4">No withdrawals yet</p>
          ) : (
            <div className="space-y-3">
              {history.map((withdrawal) => {
                const Icon = methodIcons[withdrawal.method] || Wallet;
                const statusColors = {
                  pending: { bg: "bg-[#FFD600]/20", text: "text-[#FFD600]", icon: Clock },
                  approved: { bg: "bg-[#00E701]/20", text: "text-[#00E701]", icon: CheckCircle },
                  rejected: { bg: "bg-[#FF3B30]/20", text: "text-[#FF3B30]", icon: XCircle },
                  cancelled: { bg: "bg-white/10", text: "text-white/50", icon: XCircle }
                };
                const status = statusColors[withdrawal.status] || statusColors.pending;
                const StatusIcon = status.icon;
                
                return (
                  <div key={withdrawal.withdrawal_id} className="p-4 rounded-xl bg-white/5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-white/60" />
                        </div>
                        <div>
                          <p className="text-white font-medium">₹{withdrawal.amount.toLocaleString()}</p>
                          <p className="text-[#52525B] text-xs">{formatDate(withdrawal.created_at)}</p>
                        </div>
                      </div>
                      <Badge className={`${status.bg} ${status.text} border-0`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {withdrawal.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
